import { useCallback, useEffect, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import cn from 'classnames';

import { setHeaderTitle } from 'widgets/Header';
import { useAppDispatch } from 'store';
import { ResultsListWithInfiniteScroll, SideSearchCard } from 'features/search/components';

import styles from './index.module.scss';

const MIN_SCROLLBAR_THUMB_HEIGHT = 40;

export const Results = () => {
	const dispatch = useAppDispatch();
	const containerRef = useRef<HTMLElement>(null);
	const [isScrollbarVisible, setIsScrollbarVisible] = useState(false);
	const [isScrollbarDragging, setIsScrollbarDragging] = useState(false);
	const dragRef = useRef<{ pointerStartY: number; scrollStartTop: number } | null>(null);
	const [scrollbarMetrics, setScrollbarMetrics] = useState({
		canScroll: false,
		thumbHeight: 0,
		thumbTop: 0
	});

	useEffect(() => {
		dispatch(setHeaderTitle('Search'));
	}, [dispatch]);

	const updateScrollbarMetrics = useCallback(() => {
		const container = containerRef.current;
		if (!container) return;

		const { clientHeight, scrollHeight, scrollTop } = container;
		const scrollRange = scrollHeight - clientHeight;
		const canScroll = scrollRange > 1;

		if (!canScroll) {
			setScrollbarMetrics(current =>
				current.canScroll ? { canScroll: false, thumbHeight: 0, thumbTop: 0 } : current
			);
			return;
		}

		const thumbHeight = Math.max((clientHeight / scrollHeight) * clientHeight, MIN_SCROLLBAR_THUMB_HEIGHT);
		const thumbRange = clientHeight - thumbHeight;
		const thumbTop = (scrollTop / scrollRange) * thumbRange;

		setScrollbarMetrics(current => {
			if (
				current.canScroll &&
				Math.abs(current.thumbHeight - thumbHeight) < 0.5 &&
				Math.abs(current.thumbTop - thumbTop) < 0.5
			) {
				return current;
			}

			return { canScroll: true, thumbHeight, thumbTop };
		});
	}, []);

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		updateScrollbarMetrics();

		container.addEventListener('scroll', updateScrollbarMetrics, { passive: true });
		window.addEventListener('resize', updateScrollbarMetrics);

		const resizeObserver = new ResizeObserver(updateScrollbarMetrics);
		resizeObserver.observe(container);

		if (container.firstElementChild) {
			resizeObserver.observe(container.firstElementChild);
		}

		return () => {
			container.removeEventListener('scroll', updateScrollbarMetrics);
			window.removeEventListener('resize', updateScrollbarMetrics);
			resizeObserver.disconnect();
		};
	}, [updateScrollbarMetrics]);

	const showScrollbar = useCallback(() => {
		updateScrollbarMetrics();
		setIsScrollbarVisible(true);
	}, [updateScrollbarMetrics]);

	const hideScrollbar = useCallback(() => {
		setIsScrollbarVisible(false);
	}, []);

	const scrollToScrollbarPosition = useCallback(
		(clientY: number) => {
			const container = containerRef.current;
			if (!container || !scrollbarMetrics.canScroll) return;

			const { clientHeight, scrollHeight } = container;
			const scrollRange = scrollHeight - clientHeight;
			const thumbRange = clientHeight - scrollbarMetrics.thumbHeight;
			const nextThumbTop = Math.min(Math.max(clientY - scrollbarMetrics.thumbHeight / 2, 0), thumbRange);

			container.scrollTop = thumbRange > 0 ? (nextThumbTop / thumbRange) * scrollRange : 0;
		},
		[scrollbarMetrics]
	);

	const handleScrollbarTrackMouseDown = useCallback(
		(event: ReactMouseEvent<HTMLDivElement>) => {
			if (event.target !== event.currentTarget) return;

			event.preventDefault();
			scrollToScrollbarPosition(event.clientY);
		},
		[scrollToScrollbarPosition]
	);

	const handleDocumentMouseMove = useCallback((event: MouseEvent) => {
		const container = containerRef.current;
		const dragState = dragRef.current;

		if (!container || !dragState) return;

		const { clientHeight, scrollHeight } = container;
		const scrollRange = scrollHeight - clientHeight;
		const thumbHeight = Math.max((clientHeight / scrollHeight) * clientHeight, MIN_SCROLLBAR_THUMB_HEIGHT);
		const thumbRange = clientHeight - thumbHeight;
		const scrollDelta = thumbRange > 0 ? ((event.clientY - dragState.pointerStartY) / thumbRange) * scrollRange : 0;

		container.scrollTop = dragState.scrollStartTop + scrollDelta;
	}, []);

	const handleDocumentMouseUp = useCallback(() => {
		dragRef.current = null;
		setIsScrollbarDragging(false);
		document.removeEventListener('mousemove', handleDocumentMouseMove);
		document.removeEventListener('mouseup', handleDocumentMouseUp);
	}, [handleDocumentMouseMove]);

	const handleScrollbarThumbMouseDown = useCallback(
		(event: ReactMouseEvent<HTMLDivElement>) => {
			const container = containerRef.current;
			if (!container) return;

			event.preventDefault();
			event.stopPropagation();
			dragRef.current = {
				pointerStartY: event.clientY,
				scrollStartTop: container.scrollTop
			};
			setIsScrollbarDragging(true);
			document.addEventListener('mousemove', handleDocumentMouseMove);
			document.addEventListener('mouseup', handleDocumentMouseUp);
		},
		[handleDocumentMouseMove, handleDocumentMouseUp]
	);

	useEffect(() => {
		return () => {
			document.removeEventListener('mousemove', handleDocumentMouseMove);
			document.removeEventListener('mouseup', handleDocumentMouseUp);
		};
	}, [handleDocumentMouseMove, handleDocumentMouseUp]);

	const shouldShowScrollbar = (isScrollbarVisible || isScrollbarDragging) && scrollbarMetrics.canScroll;

	return (
		<section
			className={cn(styles.container, { [styles.containerScrollbarVisible]: shouldShowScrollbar })}
			onMouseEnter={showScrollbar}
			onMouseLeave={hideScrollbar}
			ref={containerRef}>
			<div className={styles.content}>
				<SideSearchCard />
				<div className={styles.resultsWrapper} onMouseEnter={hideScrollbar} onMouseLeave={showScrollbar}>
					<ResultsListWithInfiniteScroll outerScrollContainerRef={containerRef} />
				</div>
			</div>
			<div className={styles.scrollbar} onMouseDown={handleScrollbarTrackMouseDown}>
				<div
					className={styles.scrollbarThumb}
					onMouseDown={handleScrollbarThumbMouseDown}
					style={{
						height: scrollbarMetrics.thumbHeight,
						transform: `translateY(${scrollbarMetrics.thumbTop}px)`
					}}
				/>
			</div>
		</section>
	);
};
